import { IconChevronDown } from '@tabler/icons-react';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Text, View } from '@/styles/reactnative';
import { ThemeContext } from '@/theme/ThemeProvider';
import { CButton, CInput } from '@app/components/Common';
import { COLORS } from '@app/constants';
import { useTradeModalStore } from '@app/hooks/trade/modalStore';
import BigNumber from 'bignumber.js';
import { useTradeSetting } from '../../../../../../hooks/trade/tradeSetting';
import useTradeStore from '../../../../../../hooks/trade/tradeStore';
import { CustomInput, OrderValueInputSection } from '../styles';
import { Input } from 'antd';
import { useIndexPrice } from '../../../../../../hooks/trade/indexprice';
import useGetCoinTier from '../../../../../../hooks/trade/coinTier';
import { truncateWithComma } from '@utils/Truncate.ts';
import styled from 'styled-components';
import usePercentageStore from '@/hooks/trade/percentageStore';
import { useTradePosition } from '../../../../../../hooks/trade/tradePosition';
import { useBalance } from '../../../../../../hooks/user/mybalance';
import { useTradeOrder } from '../../../../../../hooks/trade/tradeOrder';

const OrderValueInput = ({
  isManualInputRef,
  percentage,
  setPercentage,
  isMobile,
}) => {
  const { textw, gray, mobileTradeBorder, textsilver } =
    useContext(ThemeContext).theme.COLORS;
  const { t } = useTranslation();
  const {
    isDarkMode,
    toggleTheme,
    theme: { COLORS: themeColors },
  } = useContext(ThemeContext);
  const { getMaxNotionalForLeverage } = useGetCoinTier();
  const theme = isDarkMode ? 'dark' : 'light';

  const { data: tradeStore, mutate: mutateTradeStore } = useTradeStore();
  const { data: modalStore, mutate: mutateModalStore } = useTradeModalStore();

  // 포지션 및 잔고 관련 hooks 추가
  const { data: position } = useTradePosition();
  const { data: settings } = useTradeSetting(tradeStore?.ep_id);
  const { data: orders } = useTradeOrder(tradeStore?.ep_id);
  const currency = tradeStore?.pairs?.split('/')[1];
  const { data: balance } = useBalance(currency, 1);
  const { getPrice } = useIndexPrice(tradeStore?.ep_id || 0);

  // const initialOrderAmount = tradeStore?.order_amount || "0";
  const initialOrderAmount = tradeStore?.order_amount ?? '';

  const [orderAmount, setOrderAmountState] = useState(initialOrderAmount);
  const [diplayOrderAmount, setDiplayOrderAmount] = useState(0);

  const setGlobalPercentage = usePercentageStore(
    (state) => state.setPercentage
  ); // zustand setter

  // 현재 포지션 정보 가져오기
  const currentEPPos = position?.find(
    (item) => +item.exchange_pairs_id == +tradeStore?.ep_id
  );

  // 최대 수량 계산 (PercentageForm과 동일한 로직)
  const calculateMaxAmount = () => {
    const availableBalance = new BigNumber(balance?.balance || 0)
      .minus(balance?.lockbalance || 0)
      .toNumber();

    let ordermarinsum = 0;
    if (orders && orders.length > 0) {
      orders.map((item) => {
        if (item?.reduce_position == 0) {
          ordermarinsum += item?.margin;
        }
      });
    }

    let maxAmount = BigNumber(availableBalance - ordermarinsum);
    let calcprice = tradeStore?.order_price;

    if (tradeStore?.limit_market === 'M') {
      calcprice = getPrice(tradeStore?.ep_id);
    }

    if (tradeStore?.main_sub != 'S') {
      maxAmount = maxAmount.multipliedBy(
        BigNumber(settings?.leverage).dividedBy(calcprice)
      );
    } else {
      maxAmount = maxAmount.multipliedBy(BigNumber(settings?.leverage));
    }

    const maxfee = 0.1; // 임시 수수료값
    const feeprice = BigNumber(tradeStore?.order_amount || 1)
      .multipliedBy(maxfee || 1)
      .dividedBy(100)
      .multipliedBy(settings?.leverage);

    maxAmount = maxAmount
      .minus(feeprice)
      .minus(tradeStore?.main_sub !== 'S' ? 0.00001 : 2);

    // 코인 티어표를 기준으로 한 레버리지별 수량 제한 적용
    const coinSymbol = tradeStore?.pairs?.split('/')[0];

    if (coinSymbol && calcprice) {
      const currentLeverage = settings?.leverage || 1;
      const maxNotional = getMaxNotionalForLeverage(
        coinSymbol,
        currentLeverage
      );

      if (maxNotional && maxNotional !== Infinity) {
        if (tradeStore?.main_sub === 'S') {
          const maxAllowedValue = new BigNumber(maxNotional);
          maxAmount = BigNumber.minimum(maxAmount, maxAllowedValue);
        } else {
          const maxAllowedAmount = new BigNumber(maxNotional).dividedBy(
            calcprice
          );
          maxAmount = BigNumber.minimum(maxAmount, maxAllowedAmount);
        }
      }
    }

    return Math.max(maxAmount.toNumber(), 0);
  };

  // 롱/숏 수량 계산을 위한 함수 수정
  const getLongShortQuantities = () => {
    const baseAmount = percentage > 0 ? diplayOrderAmount : 0;

    // 안전한 currency 계산 (pairs에서 직접 가져오기 우선)
    const pairs = tradeStore?.pairs?.split('/');
    const coin = pairs?.[0] || '';
    const baseCurrency = pairs?.[1] || 'USDT';

    let currency;
    if (tradeStore?.main_sub !== 'S') {
      // Order By Qty일 때는 코인 심볼 사용 (pairs에서 우선 가져옴, 기본값 M)
      currency = coin || tradeStore?.coin_type || '';
    } else {
      // Order By Value일 때는 기축통화 사용
      currency = baseCurrency || tradeStore?.sub_symbol || 'USDT';
    }

    // 롱과 숏 각각에 대해 별도로 최대 수량 계산
    const calculateMaxAmountForDirection = (direction) => {
      const availableBalance = new BigNumber(balance?.balance || 0)
        .minus(balance?.lockbalance || 0)
        .toNumber();

      let ordermarinsum = 0;
      if (orders && orders.length > 0) {
        orders.map((item) => {
          if (item?.reduce_position == 0) {
            ordermarinsum += item?.margin;
          }
        });
      }

      let maxAmount = BigNumber(availableBalance - ordermarinsum);
      let calcprice = tradeStore?.order_price;

      if (tradeStore?.limit_market === 'M') {
        calcprice = getPrice(tradeStore?.ep_id);
      }

      if (tradeStore?.main_sub != 'S') {
        maxAmount = maxAmount.multipliedBy(
          BigNumber(settings?.leverage).dividedBy(calcprice)
        );
      } else {
        maxAmount = maxAmount.multipliedBy(BigNumber(settings?.leverage));
      }

      const maxfee = 0.1; // 임시 수수료값
      const feeprice = BigNumber(tradeStore?.order_amount || 1)
        .multipliedBy(maxfee || 1)
        .dividedBy(100)
        .multipliedBy(settings?.leverage);

      maxAmount = maxAmount
        .minus(feeprice)
        .minus(tradeStore?.main_sub !== 'S' ? 0.00001 : 2);

      // 코인 티어표를 기준으로 한 레버리지별 수량 제한 적용
      const coinSymbol = tradeStore?.pairs?.split('/')[0];

      if (coinSymbol && calcprice) {
        const currentLeverage = settings?.leverage || 1;
        const maxNotional = getMaxNotionalForLeverage(
          coinSymbol,
          currentLeverage
        );

        if (maxNotional && maxNotional !== Infinity) {
          if (tradeStore?.main_sub === 'S') {
            const maxAllowedValue = new BigNumber(maxNotional);
            maxAmount = BigNumber.minimum(maxAmount, maxAllowedValue);
          } else {
            const maxAllowedAmount = new BigNumber(maxNotional).dividedBy(
              calcprice
            );
            maxAmount = BigNumber.minimum(maxAmount, maxAllowedAmount);
          }
        }
      }

      // 포지션을 고려한 maxAmount 조정
      if (currentEPPos && currentEPPos.margin > 0) {
        let currentPositionAmount;

        if (tradeStore?.main_sub === 'S') {
          // Order by Value: amount에 현재가격과 레버리지를 곱해서 USDT 기준으로 변환
          currentPositionAmount = Math.abs(
            currentEPPos.amount * calcprice * (settings?.leverage || 1)
          );
        } else {
          // Order by Qty: amount에 레버리지를 곱해서 실제 포지션 크기를 구함
          currentPositionAmount = Math.abs(
            currentEPPos.amount * (settings?.leverage || 1)
          );
        }

        // 디버깅용 로그
        // console.log(`[포지션 디버깅] 방향: ${direction}, 포지션방향: ${currentEPPos.long_short}, 포지션량: ${currentPositionAmount}, 기본최대량: ${maxAmount.toNumber()}`);

        if (currentEPPos.long_short === 'L') {
          // 롱 포지션 보유중
          if (direction === 'L') {
            // 추가 롱 진입: 최대수량 - 현재포지션
            maxAmount = BigNumber.maximum(
              maxAmount.minus(currentPositionAmount),
              0
            );
          } else if (direction === 'S') {
            // 포지션 청산 + 숏 진입: 현재포지션 + 최대수량
            maxAmount = maxAmount.plus(currentPositionAmount);
          }
        } else {
          // 숏 포지션 보유중
          if (direction === 'L') {
            // 포지션 청산 + 롱 진입: 현재포지션 + 최대수량
            maxAmount = maxAmount.plus(currentPositionAmount);
          } else if (direction === 'S') {
            // 추가 숏 진입: 최대수량 - 현재포지션
            maxAmount = BigNumber.maximum(
              maxAmount.minus(currentPositionAmount),
              0
            );
          }
        }

        // console.log(`[포지션 디버깅] 조정 후 최대량: ${maxAmount.toNumber()}`);
      } else {
        // console.log(`[포지션 디버깅] 포지션 없음 또는 마진 0 - currentEPPos:`, currentEPPos);
      }

      return Math.max(maxAmount.toNumber(), 0);
    };

    const longQty = calculateMaxAmountForDirection('L');
    const shortQty = calculateMaxAmountForDirection('S');

    // 퍼센트가 0보다 클 때는 실제 계산된 수량 사용
    if (percentage > 0) {
      const calculatedLongQty = (longQty * percentage) / 100;
      const calculatedShortQty = (shortQty * percentage) / 100;

      return {
        longQty: `${truncateWithComma(calculatedLongQty, 3)} ${currency}`,
        shortQty: `${truncateWithComma(calculatedShortQty, 3)} ${currency}`,
      };
    } else {
      return {
        longQty: `${truncateWithComma(longQty, 3)} ${currency}`,
        shortQty: `${truncateWithComma(shortQty, 3)} ${currency}`,
      };
    }
  };

  const setOrderAmount = (data) => {
    if (isNaN(Number(data)) && data !== '') return;
    mutateTradeStore({ ...tradeStore, order_amount: data });
  };

  const handleInputChange = (e) => {
    let value = e.target.value;
    setGlobalPercentage(0);

    // 앞에 0이 붙은 숫자 처리 (예: 050 -> 50)
    if (value.length > 1 && value.startsWith('0') && !value.startsWith('0.')) {
      value = value.replace(/^0+/, '');
    }

    if (!isNaN(value) || value === '' || value === '.') {
      // 코인 티어표를 기준으로 수량 제한 확인
      if (value !== '' && !isNaN(Number(value))) {
        const validatedValue = validateAmountWithTier(value);
        setOrderAmountState(validatedValue);
        setOrderAmount(validatedValue);
      } else {
        setOrderAmountState(value);
        setOrderAmount(value);
      }
    }
  };

  const handleInputButton = (type: 'plus' | 'minus') => {
    const validatedValue = validateAmountWithTier(orderAmount);
    if (type === 'plus') {
      setOrderAmountState(
        Math.max(new BigNumber(validatedValue).plus(1).toNumber(), 0)
      );
      setOrderAmount(
        Math.max(new BigNumber(validatedValue).plus(1).toNumber(), 0)
      );
    } else {
      setOrderAmountState(
        Math.max(new BigNumber(validatedValue).minus(1).toNumber(), 0)
      );
      setOrderAmount(
        Math.max(new BigNumber(validatedValue).minus(1).toNumber(), 0)
      );
    }
  };

  // 코인 티어표를 기준으로 수량 검증
  const validateAmountWithTier = (inputValue) => {
    const coinSymbol = tradeStore?.pairs?.split('/')[0];
    const currentLeverage = settings?.leverage || 1;

    if (!coinSymbol) return inputValue;

    let price = tradeStore?.order_price;
    if (tradeStore?.limit_market === 'M') {
      price = getPrice();
    }

    if (!price) return inputValue;

    // 현재 레버리지에서 허용되는 최대 명목가 가져오기
    const maxNotional = getMaxNotionalForLeverage(coinSymbol, currentLeverage);

    if (maxNotional && maxNotional !== Infinity) {
      const numericValue = new BigNumber(inputValue);

      if (tradeStore?.main_sub === 'S') {
        // Order By Value: 입력값이 직접 명목가(USDT)이므로 바로 비교
        if (numericValue.isGreaterThan(maxNotional)) {
          return maxNotional.toString();
        }
      } else {
        // Order By Qty: 수량을 명목가로 변환해서 비교
        const maxAllowedAmount = new BigNumber(maxNotional).dividedBy(price);

        // 입력값이 허용 가능한 최대값을 초과하는 경우 제한
        if (numericValue.isGreaterThan(maxAllowedAmount)) {
          return maxAllowedAmount.toFixed(tradeStore?.dp2 || 5);
        }
      }
    }

    return inputValue;
  };

  //let displayOrderAmount: any = new BigNumber(orderAmount);
  let displayOrderAmount = orderAmount !== '' ? new BigNumber(orderAmount) : '';

  if (orderAmount !== '' && !isNaN(Number(orderAmount))) {
    if (displayOrderAmount instanceof BigNumber) {
      displayOrderAmount =
        tradeStore?.main_sub === 'M'
          ? displayOrderAmount.toFixed(tradeStore?.dp2 || 5)
          : displayOrderAmount.toFixed(4);
    }
  }

  // displayOrderAmount = isQty
  //   ? displayOrderAmount.toFixed(5)
  //   : displayOrderAmount.toFixed(3);

  // useEffect(() => {
  //   setOrderAmountState(displayOrderAmount);
  // }, [displayOrderAmount]);

  //
  // useEffect(() => {
  //   setOrderAmountState(tradeStore?.order_amount || "0");
  // }, [tradeStore?.order_amount]);

  useEffect(() => {
    if (tradeStore?.order_amount === 0) {
      setOrderAmountState(tradeStore?.order_amount);
    }

    // if(!isManualInputRef?.current) return

    if (tradeStore?.order_amount !== undefined) {
      setDiplayOrderAmount(tradeStore?.order_amount);
    }
  }, [tradeStore?.order_amount]);

  return isMobile ? (
    <OrderValueInputSection style={{ gap: 4 }}>
      <CButton
        title={`${tradeStore?.main_sub !== 'S' ? t('qty') : t('orderValue')} (${
            tradeStore?.main_sub !== 'S'
              ? (tradeStore?.pairs?.split('/')[0] || tradeStore?.coin_type || '')
              : (tradeStore?.pairs?.split('/')[1] || tradeStore?.sub_symbol || 'USDT')
          })`}
        onClick={() => {
          mutateModalStore({
            ...modalStore,
            orderPlace: true,
          });
        }}
        style={{
          alignSelf: 'flex-start',
          color: '#B3B5BD',
          fontSize: 12,
          fontFamily: 'NanumSquareRound',
          fontWeight: 400,
          letterSpacing: -0.36,
          cursor: 'pointer',
        }}
        icon={
          <IconChevronDown
            size={14}
            color={COLORS.lightGray5}
            style={{ marginLeft: 5 }}
          />
        }
      />
      <CustomInput
        placeholder={''}
        maxLength={10}
        value={percentage > 0 ? percentage + '%' : orderAmount}
        type={'text'}
        onChange={handleInputChange}
        suffix={
          <div
            style={{
              color: '#E9E9ED',
              fontFamily: 'NanumSquareRound',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: -0.24,
            }}
          >
            { tradeStore?.main_sub !== 'S'
            ? (tradeStore?.pairs?.split('/')[0] || tradeStore?.coin_type || '')
            : (tradeStore?.pairs?.split('/')[1] || tradeStore?.sub_symbol || 'USDT')}
          </div>
        }
        onFocus={() => {
          setPercentage(0);

          if (tradeStore?.order_amount > 0) {
            mutateTradeStore({
              ...tradeStore,
              order_amount: 0,
            });
          }
        }}
      />
    </OrderValueInputSection>
  ) : (
    <OrderValueInputSection style={{ gap: 4 }}>
      <CButton
        title={`${tradeStore?.main_sub !== 'S' ? t('qty') : t('orderValue')} (${
            tradeStore?.main_sub !== 'S'
              ? (tradeStore?.pairs?.split('/')[0] || tradeStore?.coin_type || '')
              : (tradeStore?.pairs?.split('/')[1] || tradeStore?.sub_symbol || 'USDT')
          })`}
        onClick={() => {
          mutateModalStore({
            ...modalStore,
            orderPlace: true,
          });
        }}
        style={{
          alignSelf: 'flex-start',
          color: '#B3B5BD',
          fontSize: 12,
          fontFamily: 'NanumSquareRound',
          fontWeight: 400,
          letterSpacing: -0.36,
          cursor: 'pointer',
        }}
        icon={
          <IconChevronDown
            size={14}
            color={COLORS.lightGray5}
            style={{ marginLeft: 5 }}
          />
        }
      />

      {/* {percentage > 0 && (
          <QuantityDisplayContainer>
            <QuantityItem>
              <QuantityLabel>{t('long')}</QuantityLabel>
              <QuantityValue color="#4CAF50">
                {getLongShortQuantities().longQty}
              </QuantityValue>
            </QuantityItem>
            <QuantityDivider>|</QuantityDivider>
            <QuantityItem>
              <QuantityLabel>{t('short')}</QuantityLabel>
              <QuantityValue color="#F44336">
                {getLongShortQuantities().shortQty}
              </QuantityValue>
            </QuantityItem>
          </QuantityDisplayContainer>
        )} */}
      <CustomInput
        placeholder={''}
        maxLength={10}
        value={percentage > 0 ? percentage + '%' : orderAmount}
        type={'text'}
        onChange={handleInputChange}
        suffix={
          <div
            style={{
              color: '#E9E9ED',
              fontFamily: 'NanumSquareRound',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: -0.24,
            }}
          >
            { tradeStore?.main_sub !== 'S'
            ? (tradeStore?.pairs?.split('/')[0] || tradeStore?.coin_type || '')
            : (tradeStore?.pairs?.split('/')[1] || tradeStore?.sub_symbol || 'USDT')}
          </div>
        }
        onFocus={() => {
          setPercentage(0);

          if (tradeStore?.order_amount > 0) {
            mutateTradeStore({
              ...tradeStore,
              order_amount: 0,
            });
          }
        }}
      />
    </OrderValueInputSection>
  );
};

export default React.memo(OrderValueInput);

const QuantityDisplayContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
`;

const QuantityItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

const QuantityLabel = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.text || '#666'};
  font-weight: 500;
`;

const QuantityValue = styled.div`
  font-size: 11px;
  color: ${(props) => props.color};
  font-weight: 600;
`;

const QuantityDivider = styled.div`
  color: ${({ theme }) => theme.text || '#666'};
  font-size: 12px;
  opacity: 0.5;
`;

const MContainer = styled.div<{ isDarkMode: boolean }>`
  border-radius: 6px;
  margin-top: 15px;
  background: gray;
  color: textw;
  margintop: 5;
  padding: 4px 12px;
  width: 100%;
  background-color: ${({ isDarkMode }) => (isDarkMode ? '#0C110E' : '#F8F4F0')};
  display: flex;
  justify-content: space-between;
  gap: 8px;
  align-items: center;
`;
